/**
 * Editor selectors — Screen editor testid registry.
 *
 * Single source of truth for all screen editor data-testid values
 * used across V1 and V2 E2E test helpers.
 */

export const EDITOR = {
  // ── Editor Toolbar ──
  saveBtn: 'editor-save-btn',
  doneBtn: 'editor-done-btn',
  closeBtn: 'editor-close-btn',
  languageSelect: 'editor-language-select',

  // ── V2 Canvas Container (the FULL canvas page; stays visible while canvas is open) ──
  modalV2Container: 'editor-modal-v2-container',
  deviceMockup: 'device-mockup',
  previewMeasureRoot: 'editor-preview-measure-root',

  // ── V2 Screen Editor Dialog (the per-screen editor dialog; only open during editing) ──
  screenEditorDialog: 'screen-editor-dialog',

  // ── V2 Editor Dialog Panels (3-panel layout, inside screenEditorDialog) ──
  controlPanel: 'editor-control-panel',
  previewPanel: 'editor-preview-panel',
  contentPanel: 'editor-content-panel',
  devicePreview: 'device-mockup',

  // ── Unsaved Changes Dialog ──
  unsavedDialog: 'unsaved-changes-dialog',
  unsavedDiscardBtn: 'unsaved-discard-btn',
  unsavedCancelBtn: 'unsaved-cancel-btn',
  unsavedSaveBtn: 'unsaved-save-btn',

  // ── QuickEdit Popover ──
  quickEditPopover: 'quick-edit-popover',

  // ── Mention / Variable Autocomplete (TiptapTemplateEditor) ──
  mentionMenu: 'mention-suggestion-menu',

  // ── Backdrop Section ──
  backdropChromeColor: 'backdrop-chrome-color',

  // ── Options/List ──
  // NOTE: the DOM renders btn-options-add (duplicate data-testid removed 2026-07; last-wins had already made this the live value)
  addListItemBtn: 'btn-options-add',

  // ── AI Assets ──
  aiAssets: {
    section: 'ai-assets-section',
    contextView: (slotKey: string) => `ai-asset-context-view-${slotKey}`,
    contextPanel: (slotKey: string) => `ai-asset-context-panel-${slotKey}`,
    contextInject: (slotKey: string) => `ai-asset-context-inject-${slotKey}`,
  },

  // ── AI Meta: the screen evidence panel «Why this copy» (W13 DM2) ──
  // One row per CITED thesis, each carrying its quote citations as sub-rows.
  // The ids come from the sourcePath grammar — the path is normative (W13-R17),
  // so a row's key is the thesis id and a sub-row's the `originalItemId`.
  aiMetaEvidence: {
    section: 'ai-meta-evidence',
    /** Template: `ai-meta-evidence-thesis-${insightId}` */
    thesisRow: (insightId: string) => `ai-meta-evidence-thesis-${insightId}`,
    /** Template: `ai-meta-evidence-quote-${insightId}-${sourceItemId}` (indented under its thesis). */
    quoteRow: (insightId: string, sourceItemId: string) =>
      `ai-meta-evidence-quote-${insightId}-${sourceItemId}`,
    /** Heading of the «Quote without its thesis» group — a lone quote is never dropped. */
    loneQuoteHeading: 'ai-meta-evidence-lone-quote-heading',
    /** Template: `ai-meta-evidence-lone-quote-${sourceItemId}` */
    loneQuoteRow: (sourceItemId: string) => `ai-meta-evidence-lone-quote-${sourceItemId}`,
  },

  // ── Screen Name (inline edit — shared V1/V2) ──
  nameEditBtn: 'screen-name-edit-button',
  nameInput: 'screen-name-input',
  nameConfirmBtn: 'screen-name-confirm-button',

  // ── Sections (accordion testids — shared V1/V2) ──
  section: {
    content: 'section-content',
    options: 'section-options',
    field: 'section-field',
    header: 'section-header',
    variableBinding: 'section-variable-binding',
    paywallSubscriptions: 'section-paywall-subscriptions',
    animation: 'section-animation',
  },

  // ── V2 Section Accordions (getSectionTestId output) ──
  v2Section: {
    headerBar: 'section-header',
    actionBar: 'section-action-bar',
    backdrop: 'section-background',
    copyBlock: 'section-content',
    basicConfig: 'section-basic-config',
    animation: 'section-animation',
    media: 'section-media',
    options: 'section-options',
    variableBinding: 'section-variable-binding',
    textField: 'section-text-field',
    carousel: 'section-carousel',
    stepper: 'section-stepper',
    roller: 'section-roller',
    embed: 'section-embed',
    paywallHeader: 'section-paywall-header',
    paywallMedia: 'section-paywall-media',
    paywallBody: 'section-paywall-body',
    paywallSubscriptions: 'section-paywall-subscriptions',
    paywallFooter: 'section-paywall-footer',
    flexibleSections: 'section-flexible-sections',
    stickyContainer: 'section-sticky-container',
  },

  // ── V2 Field-Level TestIds (getFieldTestId / getCheckboxTestId / etc.) ──
  v2Field: {
    // HeaderBar
    backButton: 'field-header-back-button',
    backButtonCheckbox: 'checkbox-header-back-button',
    backButtonInput: 'input-header-back-button',
    backButtonStyleToggle: 'styles-toggle-header-back-button',
    backButtonStyleGroup: 'styles-group-header-back-button',
    skipButton: 'field-header-skip-button',
    skipButtonCheckbox: 'checkbox-header-skip-button',
    skipButtonInput: 'input-header-skip-button',
    skipButtonStyleToggle: 'styles-toggle-header-skip-button',
    skipButtonStyleGroup: 'styles-group-header-skip-button',
    pageIndicator: 'field-header-page-indicator',
    pageIndicatorCheckbox: 'checkbox-header-page-indicator',
    pageIndicatorStyleToggle: 'styles-toggle-header-page-indicator',
    pageIndicatorSlider: 'slider-header-page-indicator',
    pageIndicatorInput: 'input-header-page-indicator',
    // Header — progress-indicator style panel (revealed by pageIndicatorStyleToggle)
    pageIndicatorKindSelect: 'select-header-page-indicator-kind',
    pageIndicatorActiveColorInput: 'input-style-page-indicator-page-indicator-color',
    pageIndicatorTrackColorInput: 'input-style-page-indicator-page-indicator-track',
    pageIndicatorTitleInput: 'input-page-indicator-title',
    pageIndicatorTitleCheckbox: 'checkbox-page-indicator-title',
    pageIndicatorTitleStyleToggle: 'styles-toggle-page-indicator-title',
    pageIndicatorTitleStyleGroup: 'styles-group-page-indicator-title',
    pageIndicatorIconGroup: 'button-style-section-icon',
    pageIndicatorIconEnable: 'style-group-icon-enable',
    pageIndicatorContentAlignmentSelect: 'content.header.progressIndicator.contentAlignment-select',
    progressFullWidthToggle: 'toggle-header-progress-full-width',
    progressFullWidthRow: 'row-header-progress-full-width',
    progressRespectButtonsToggle: 'toggle-header-progress-respect-buttons',
    progressRespectButtonsRow: 'row-header-progress-respect-buttons',
    progressVerticalAlignSelect: 'select-header-progress-vertical-align',
    progressVerticalAlignRow: 'row-header-progress-vertical-align',
    progressInsetsGroup: 'content.header.progressLayout.insets',
    progressInsetsPaddingTop: 'content.header.progressLayout.insets.paddingTop',
    // Header — appearance panel, revealed by the "Toggle header settings" gear
    // (data-testid added 2026-09-04, AC-169 recon: was previously reachable only
    // via aria-label — see V2HeaderBarSection.tsx `getStylesToggleTestId(SECTION_NAME, 'Header Settings')`)
    headerSettingsToggle: 'styles-toggle-header-header-settings',
    headerAppearanceHeightInput: 'input-content.header.appearance.height',
    headerAppearanceBackgroundColorInput: 'input-content.header.appearance.backgroundColor',
    headerAppearanceOpacityInput: 'input-content.header.appearance.opacity',
    headerInsetsGroup: 'content.header.insets',
    headerInsetsPaddingTop: 'content.header.insets.paddingTop',
    // ActionBar
    primaryButton: 'field-action-bar-primary-button',
    primaryButtonCheckbox: 'checkbox-action-bar-primary-button',
    primaryButtonInput: 'input-action-bar-primary-button',
    primaryButtonStyleToggle: 'styles-toggle-action-bar-primary-button',
    secondaryButton: 'field-action-bar-secondary-button',
    secondaryButtonCheckbox: 'checkbox-action-bar-secondary-button',
    secondaryButtonInput: 'input-action-bar-secondary-button',
    secondaryButtonStyleToggle: 'styles-toggle-action-bar-secondary-button',
    // ActionBar — block-article text-style-group wrappers (the per-button label text styling grid)
    primaryButtonStyleGroup: 'styles-group-action-bar-primary-button',
    secondaryButtonStyleGroup: 'styles-group-action-bar-secondary-button',
    // CopyBlock
    heroImage: 'field-content-hero-image',
    heroImageCheckbox: 'checkbox-content-hero-image',
    heroImageInput: 'input-content-hero-image',
    heroImageUpload: 'upload-btn-content-hero-image',
    heroImageStyleToggle: 'styles-toggle-content-hero-image',
    heroImageLayoutInput: 'input-style-hero-image-image-layout',
    heroImageScaleModeInput: 'input-style-hero-image-scale-mode',
    heroImageScaleModeCheckbox: 'checkbox-style-hero-image-scale-mode-input',
    heroImageWidthInput: 'input-style-hero-image-width',
    heroImageWidthCheckbox: 'checkbox-style-hero-image-width-input',
    heroImageHeightInput: 'input-style-hero-image-height',
    heroImageHeightCheckbox: 'checkbox-style-hero-image-height-input',
    heroImageHeightPercentageInput: 'input-style-hero-image-height-',
    heroImageHeightPercentageCheckbox: 'checkbox-style-hero-image-height--input',
    heroImageCornerRadiusInput: 'input-style-hero-image-corner-radius',
    heroImageCornerRadiusCheckbox: 'checkbox-style-hero-image-corner-radius-input',
    title: 'field-content-title',
    titleCheckbox: 'checkbox-content-title',
    titleInput: 'input-content-title',
    titleStyleToggle: 'styles-toggle-content-title',
    // CopyBlock — title "line through color" nullable checkbox (revealed by
    // titleStyleToggle; BUG-024 — currently non-nullable, added ahead of the fix
    // for the RED AC-120b spec, see Task 2 of the theme-v2-bugs-2026-08-23 plan).
    titleLineThroughCheckbox: 'checkbox-style-title-line-through-color-input',
    // CopyBlock — title "line through color" nullable color field itself (wrapper
    // testid; the native <input type="color"> is nested inside — same convention as
    // every other V2 color field). Added for AC-120b/AC-011 (fix wave 2026-09-04):
    // a non-default line-through color must render verbatim, not just toggle on/off.
    titleLineThroughColorInput: 'input-style-title-line-through-color',
    subtitle: 'field-content-subtitle',
    subtitleCheckbox: 'checkbox-content-subtitle',
    subtitleInput: 'input-content-subtitle',
    subtitleStyleToggle: 'styles-toggle-content-subtitle',
    // CopyBlock — block-article style-group wrappers + padding elements.
    titleStyleGroup: 'styles-group-content-title',
    subtitleStyleGroup: 'styles-group-content-subtitle',
    titlePaddingElement: 'content.copy.title.layout.insets',
    subtitlePaddingElement: 'content.copy.subtitle.layout.insets',
    heroPaddingElement: 'content.hero.layout.insets',
    detail: 'field-content-detail',
    detailCheckbox: 'checkbox-content-detail',
    detailInput: 'input-content-detail',
    detailStyleToggle: 'styles-toggle-content-detail',
    // Backdrop
    backdropKindSelect: 'backdrop-kind-select',
    backdropSolidColor: 'backdrop-solid-color',
    backdropImage: 'field-backdrop-image',
    backdropVideo: 'field-backdrop-video',
    backdropChromeColorField: 'backdrop-chrome-color',
    // BasicConfig
    permission: 'field-basic-config-permission',
    permissionCheckbox: 'checkbox-basic-config-permission',
    permissionInput: 'input-basic-config-permission',
    countdown: 'field-basic-config-countdown',
    countdownCheckbox: 'checkbox-basic-config-countdown',
    countdownInput: 'input-basic-config-countdown',
    countdownUnitInput: 'input-basic-config-countdown-unit',
    animationToggle: 'toggle-element-content.animated',
    offlineFirstToggle: 'toggle-element-content.offlineFirst',
    // Animation section (dedicated motion section — section-animation)
    screenEntrance: 'field-animation-screen-entrance',
    screenEntranceCheckbox: 'checkbox-animation-screen-entrance',
    screenEntrancePresetInput: 'input-animation-screen-entrance-preset',
    screenEntranceDurationInput: 'input-animation-screen-entrance-duration',
    screenEntranceStaggerInput: 'input-animation-screen-entrance-stagger',
    animationInheritedHint: 'animation-inherited-hint',
    // ToggleElement derives its testid from the field path (passed data-testid prop is not applied)
    animationLegacyToggle: 'toggle-element-content.animated',
    animationAdvancedToggle: 'animation-advanced-toggle',
    animationAdvanced: 'animation-advanced',
    animationEasingInput: 'input-animation-easing',
    animationSlotSelect: (slotKey: string) => `input-animation-slot-${slotKey}`,
    previewReplayBtn: 'editor-preview-replay-btn',
    // Options — list animations: the LAST Options List style section "Animations" (behind the
    // style gear, owner 2026-09-29), four standard StyleFieldRow fields — `checkbox-style-animations-*`
    // gates `input-style-animations-*`. The old loose `field-/checkbox-/select-options-*-animation*`
    // rows are gone. Section root: EDITOR.optionsAnimationsSection.
    optionsItemAnimationCheckbox: 'checkbox-style-animations-item-animation',
    optionsItemAnimationInput: 'input-style-animations-item-animation',
    optionsItemStaggerCheckbox: 'checkbox-style-animations-stagger',
    optionsItemStaggerInput: 'input-style-animations-stagger',
    optionsSelectionAnimationCheckbox: 'checkbox-style-animations-selection-animation',
    optionsSelectionAnimationPreset: 'input-style-animations-selection-animation',
    optionsDeselectionAnimationCheckbox: 'checkbox-style-animations-deselection-animation',
    optionsDeselectionAnimationPreset: 'input-style-animations-deselection-animation',
    // TextField
    fieldType: 'input-text-field-field-type',
    placeholder: 'field-text-field-placeholder',
    placeholderCheckbox: 'checkbox-text-field-placeholder',
    placeholderInput: 'input-text-field-placeholder',
    placeholderStyleToggle: 'styles-toggle-text-field-placeholder',
    validationRegex: 'input-text-field-validation-regex',
    validationRegexCheckbox: 'checkbox-text-field-validation-regex',
    errorMessage: 'field-text-field-error-message',
    errorMessageCheckbox: 'checkbox-text-field-error-message',
    errorMessageInput: 'input-text-field-error-message',
    errorMessageStyleToggle: 'styles-toggle-text-field-error-message',
    icon: 'field-text-field-icon',
    iconCheckbox: 'checkbox-text-field-icon',
    iconStyleToggle: 'styles-toggle-text-field-icon',
    // Media
    mediaImage: 'field-media-image',
    mediaImageUpload: 'upload-btn-media-image',
    mediaImagePreview: 'preview-media-image',
    mediaSettingsToggle: 'media-settings-toggle',
    mediaTopInput: 'input-style-media-media-top',
    mediaTopCheckbox: 'checkbox-style-media-media-top-input',
    mediaBottomInput: 'input-style-media-media-bottom',
    mediaBottomCheckbox: 'checkbox-style-media-media-bottom-input',
    mediaScaleModeInput: 'input-style-media-scale-mode',
    mediaScaleModeCheckbox: 'checkbox-style-media-scale-mode-input',
    mediaHeightPercentageInput: 'input-style-media-height-',
    mediaHeightPercentageCheckbox: 'checkbox-style-media-height--input',
    mediaCornerRadiusInput: 'input-style-media-corner-radius',
    mediaCornerRadiusCheckbox: 'checkbox-style-media-corner-radius-input',
    mediaRepeatInput: 'input-style-media-repeat',
    mediaRepeatCheckbox: 'checkbox-style-media-repeat-input',
    // Media — block-article controls (kind select, enable, video, gradient).
    mediaEnableCheckbox: 'media-enable-checkbox',
    mediaKindSelect: 'media-kind-select',
    mediaVideo: 'field-media-video',
    mediaPaddingElement: 'media-padding-element',
    mediaGradientEnable: 'media-gradient-enable',
    mediaGradientHeightSlider: 'media-gradient-height-slider',
    // The gradient color StyleFieldRow has no explicit inputTestId, so it emits the
    // default `input-<path>` testid (confirmed live).
    mediaGradientColorInput: 'input-content.featuredMedia.appearance.gradient.colors.0',
    // Stepper — Title styles panel font-family (BUG-025). The panel's other fields
    // use the inline `input-style-${scope}-${label}` template directly in
    // stepper.spec.ts (scope='title-styles'); these two are registered here because
    // AC-335b needs a stable EDITOR reference, not a literal string, per test-kit
    // convention (`input-style-<section>-<label>` / `checkbox-style-<section>-<label>-input`).
    stepperTitleFontFamilyInput: 'input-style-title-styles-font-family',
    stepperTitleFontFamilyCheckbox: 'checkbox-style-title-styles-font-family-input',
    // Stepper — Subtitle/Description styles panels font-family (fix wave 2026-09-04,
    // AC-335b coverage gap). Same `createV2TextStyleFields` shape as Title above, just
    // scoped to the 'subtitle-styles'/'description-styles' sub-sections.
    stepperSubtitleFontFamilyInput: 'input-style-subtitle-styles-font-family',
    stepperSubtitleFontFamilyCheckbox: 'checkbox-style-subtitle-styles-font-family-input',
    stepperDescriptionFontFamilyInput: 'input-style-description-styles-font-family',
    stepperDescriptionFontFamilyCheckbox: 'checkbox-style-description-styles-font-family-input',
    // Carousel + Options-item — Title styles panel font-family (Task 6, theme-v2-bugs
    // 2026-08-23 batch). Both createV2CarouselStyleSections and
    // createV2OptionsListStyleSections use section id 'titleStyles' (same as Stepper's),
    // so the emitted testids are byte-identical to stepperTitleFontFamilyInput/Checkbox
    // above — duplicated here under a screen-specific name per this file's existing
    // convention (see carouselTitleStylesSection vs stepperTitleStylesSection below).
    carouselTitleFontFamilyInput: 'input-style-title-styles-font-family',
    carouselTitleFontFamilyCheckbox: 'checkbox-style-title-styles-font-family-input',
    optionsItemTitleFontFamilyInput: 'input-style-title-styles-font-family',
    optionsItemTitleFontFamilyCheckbox: 'checkbox-style-title-styles-font-family-input',
  },

  // ── Content Fields (contentEditable wrappers) ──
  fieldContentTitle: 'field-content-title',
  fieldContentSubtitle: 'field-content-subtitle',

  // ── V1 Field Type ──
  fieldTypeInput: 'input-field-field-type',

  // ── V2 Options Dropdowns ──
  selectSelectionMode: 'select-options-selection-mode',
  selectColumnLayout: 'select-options-column-layout',
  selectItemLayout: 'select-options-item-layout',
  styleSettingsBtn: 'style-settings-button',
  styleSectionsContainer: 'style-sections-container',
  addOptionsItem: 'btn-options-add',
  // Options style sections — auto-emitted by V2ListStyleSections inside the style gear.
  // Inputs are kebab `input-style-<section>-<field>`; accordion roots are camelCase `style-section-<id>`.
  optionsRandomizeInput: 'input-style-selection-behavior-randomize-order',
  optionsMaxSelectionsInput: 'input-style-selection-behavior-max-selections',
  optionsVerticalAlignInput: 'input-style-list-position-vertical-alignment',
  optionsMaxCellHeightInput: 'input-style-list-position-max-cell-height',
  optionsUnselectedStateSection: 'style-section-unselectedItemState',
  optionsSelectedStateSection: 'style-section-selectedItemState',
  // Item-state outline widths (numberCombo — the testid sits on the <input> itself;
  // paths content.options.appearance.borders.width / content.options.selectedAppearance.borders.width).
  unselectedBorderWidthInput: 'input-style-unselected-item-state-border-width',
  selectedBorderWidthInput: 'input-style-selected-item-state-border-width',
  optionsItemPaddingsSection: 'style-section-itemPaddings',
  optionsListPaddingsSection: 'style-section-listPaddings',
  optionsItemsSpacingSection: 'style-section-itemsSpacing',
  optionsTitleStylesSection: 'style-section-titleStyles',
  optionsSubtitleStylesSection: 'style-section-subtitleStyles',
  optionsImageStylesSection: 'style-section-imageStyles',
  optionsCellDimensionsSection: 'style-section-cellDimensions',
  optionsImageContainerSection: 'style-section-imageContainer',
  optionsTextContainerSection: 'style-section-textContainer',
  optionsCheckboxContainerSection: 'style-section-checkboxContainer',
  optionsCheckboxStylesSection: 'style-section-checkboxStyles',
  // List animations — the LAST style section (collapsed by default; fields in EDITOR.v2Field.options*Animation*)
  optionsAnimationsSection: 'style-section-animations',
  addSliderItem: 'slider-add-item-button',
  sliderPlayBtn: 'slider-play-button',
  carouselStylesGear: 'carousel-styles-gear',
  carouselStyleSectionsContainer: 'carousel-style-sections-container',
  carouselTimerInput: 'carousel-timer-input',
  carouselItemsContainer: 'carousel-items-container',
  carouselTypeSelector: 'carousel-type-selector',
  // Carousel per-slide style accordions (V2ListStyleSections emits `style-section-<id>`;
  // ids from createV2CarouselStyleSections + createV2ContainerSections — the container
  // ids carry the `Settings` suffix, distinct from the Options card-region ids).
  carouselTitleStylesSection: 'style-section-titleStyles',
  carouselSubtitleStylesSection: 'style-section-subtitleStyles',
  carouselDetailStylesSection: 'style-section-detailStyles',
  carouselImageStylesSection: 'style-section-imageStyles',
  carouselImageContainerSection: 'style-section-imageContainerSettings',
  carouselTextContainerSection: 'style-section-textContainerSettings',
  stepperStylesGear: 'stepper-styles-gear',
  stepperStyleSectionsContainer: 'stepper-style-sections-container',
  stepperKindSelect: 'stepper-kind-select',
  stepperTimerInput: 'stepper-timer-input',
  stepperItemsContainer: 'stepper-items-container',
  // Stepper per-step style accordions (V2ListStyleSections emits `style-section-<id>`;
  // ids from createV2StepperStyleSections). Same `style-section-titleStyles/…` strings as
  // Carousel, but on the ProgressSteps screen (no runtime collision — different screens).
  stepperBarStylesSection: 'style-section-stepperBarStyles',
  stepperImageContainerStylesSection: 'style-section-imageContainerStyles',
  stepperTitleStylesSection: 'style-section-titleStyles',
  stepperSubtitleStylesSection: 'style-section-subtitleStyles',
  stepperDescriptionStylesSection: 'style-section-descriptionStyles',
  stepperImageStylesSection: 'style-section-imageStyles',
  // Roller (WheelPicker) style sections — behind EDITOR.styleSettingsBtn inside EDITOR.v2Section.roller
  // (V2ListStyleSections, ids from createV2RollerStyleSections; Label styles is collapsed by default).
  rollerLabelStylesSection: 'style-section-labelStyles',
  // Color field wrapper (the hex <input type="text"> is nested); path content.roller.labelAppearance.color.
  rollerLabelTextColorInput: 'input-style-label-styles-text-color',
  progressMediaContainerView: 'view-progress-steps-media-container',
  progressChartContainerView: 'view-progress-steps-chart-container',
  /** Slider item field: append `{index}-title` / `{index}-subtitle1` / `{index}-subtitle2` */
  sliderItemPrefix: 'slider-item-',
  /** Progress item field: append `{index}-title` / `{index}-subtitle` / `{index}-description` */
  progressBarItemPrefix: 'progressbar-item-',

  // ── V2 Button Style Groups (rendered by ButtonStyleSections, e.g. inside action bar primary button) ──
  buttonStyleGroup: {
    container: 'button-style-section-container',
    gradient:  'button-style-section-gradient',
    shadow:    'button-style-section-shadow',
    effects:   'button-style-section-effects',
    icon:      'button-style-section-icon',
  },

  // ── Enable / level controls INSIDE the optional button-style sections (custom editors:
  // GradientEditor / ShadowEditor / EffectsEditor / IconEditor). These features are OFF by
  // default — toggling the enable checkbox reveals their inner controls. Used by the
  // action-bar help recipe to capture each optional section OPEN with its real controls
  // (draft-only — reverts on reload). The gradient level toggle switches Simple/Medium/Advanced. ──
  buttonStyleGroupControl: {
    gradientEnable: 'style-group-gradient-enable',
    gradientLevel:  'style-group-gradient-level',
    shadowEnable:   'style-group-shadow-enable',
    effectsEnable:  'style-group-effects-enable',
    iconEnable:     'style-group-icon-enable',
  },

  // ── V2 List Item Editor ──
  /** V2 list item editor: append index (e.g., `listItemEditorPrefix + '0'`) */
  listItemEditorPrefix: 'v2-list-item-editor-',
  listItemDeletePrefix: 'v2-list-item-delete-',
  /** Title field of a list item — use instead of `.locator('input').first()` which
   *  picks up the image URL input in Image+Title layouts. Append index. */
  listItemTitlePrefix: 'v2-list-item-title-',
  /** Subtitle field of a list item — append index. */
  listItemSubtitlePrefix: 'v2-list-item-subtitle-',
  listItemVarvalueEditPrefix: 'v2-list-item-varvalue-edit-',
  listItemVarvalueInputPrefix: 'v2-list-item-varvalue-input-',
  listItemVarvalueConfirmPrefix: 'v2-list-item-varvalue-confirm-',

  // ── Variables ──
  variableSelector: 'variable-selector',
  variableCreateBtn: 'variable-create-btn',
  variableNameInput: 'variable-name-input',
  variableTypeSelect: 'variable-type-select',
  variableCreateConfirmBtn: 'variable-create-confirm-btn',
  variableDescriptionInput: 'variable-description-input',
  variableOptionsList: 'variable-options-list',
  /** Variable option row: append option UUID */
  variableOptionRowPrefix: 'variable-option-row-',
  /** Value-mapping table (option label + per-screen item binding) — first row carries these */
  variableOptionsSection: 'variable-options-section',
  variableOptionLabel: 'variable-option-label',
  variableOptionItemBinding: 'variable-option-item-binding',
  variableNewOptionInput: 'variable-new-option-input',
  variableApplyItemsBtn: 'variable-apply-items-btn',
  /** Score Effects (advanced) — collapsible header + first effect row's target/score */
  variableScoreEffectsHeader: 'variable-score-effects-header',
  variableScoreTargetSelect: 'variable-score-target-select',
  variableScoreInput: 'variable-score-input',

  // ── Header Section ──
  skipButtonCheckbox: 'checkbox-header-skip-button-input',

  // ── FlexibleLayout Sections ──
  flexibleSections: {
    addBtn: 'sections-add-btn',
    menuItemPrefix: 'sections-menu-',
    rowPrefix: 'section-row-',
    rowDeletePrefix: 'section-row-delete-',
    rowEditPrefix: 'section-row-edit-',
    rowSettingsPrefix: 'section-row-settings-',
    deleteConfirmDialog: 'section-delete-confirm-dialog',
  },

  // ── ArticleIndex settings (article editor, inline section editor) ──
  articleIndex: {
    root: 'article-index-settings',
    heading: 'article-index-heading',
    /** "Show" select: All articles / Articles in a folder / Articles on a topic */
    filterKind: 'article-index-filter',
    folderSelect: 'article-index-folder',
    topicSelect: 'article-index-topic',
    sortSelect: 'article-index-sort',
    limitInput: 'article-index-limit',
    /** "Show for each article" checkbox input — name: showThumbnail | showTopic | showDate | showDescription */
    toggle: (name: string) => `article-index-toggle-${name}`,
  },

  // ── FlexibleLayout Screen-Level & Layout Settings ──
  flexibleLayout: {
    screenSettingsGear: 'flex-screen-settings-gear',
    scrollableToggle: 'flex-screen-scrollable-toggle',
    purchaseButtonLinkedSection: 'select-purchase-button-linked-section',
    textSection: {
      accordion: 'section-text',
      titleCheckbox: 'checkbox-text-title',
      titleCheckboxInput: 'checkbox-text-title-input',
      titleInput: 'input-text-title',
    },
    layoutField: {
      heightKind: 'layout-heightKind',
      heightValue: 'layout-heightValue',
      flexGrow: 'layout-flexGrow',
      flexShrink: 'layout-flexShrink',
      scrollable: 'layout-scrollable',
      zIndex: 'layout-zIndex',
      // Width group (v2.19.0)
      widthKind: 'layout-widthKind',
      widthValue: 'layout-widthValue',
      maxWidth: 'layout-maxWidth',
      minWidth: 'layout-minWidth',
      sectionHorizontalAlignment: 'layout-sectionHorizontalAlignment',
    },
    /** Accordion groups inside SectionLayoutSettings */
    layoutGroup: {
      sizeAndFlex: 'section-layout-sizeAndFlex',
      width: 'section-layout-width',
      appearance: 'section-layout-appearance',
      spacingAlignment: 'section-layout-spacingAlignment',
    },
    /** Padding/margin editors inside the Spacing & Alignment group */
    layoutPadding: 'flexible-section-layout-padding',
    layoutMargin: 'flexible-section-layout-margin',
    /** Per-profile override badge on section layout fields (v2.19.1) — suffix: dot-path with dashes */
    layoutOverrideBadgePrefix: 'layout-override-badge-',
    /** Article presentation controls (v2.19.0/2.20.0) */
    presentation: {
      deviceSelect: 'editor-device-select',
      customSizeOption: 'device-option-custom-size',
      /** Standard device menu item — suffix: iphone|iphone-safari|android|tablet|laptop|desktop.
       * Article docs in viewport mode list ONLY the active profile's class (device↔profile sync, R6a). */
      deviceOptionPrefix: 'device-option-',
      /** Catalog preset menu item — suffix: presetRef with '/' → '-' */
      containerPresetPrefix: 'container-preset-',
      containerWidthInput: 'container-width-input',
      containerHeightInput: 'container-height-input',
      /** Chip row; HIDDEN when the screen has a fixed container (v2.20.0) */
      profileSwitcher: 'presentation-profile-switcher',
      /** Suffix: base|phone|tablet|laptop */
      profileChipPrefix: 'profile-chip-',
      profileModeHint: 'profile-mode-hint',
    },
    /** Screen-level "Default section layout" block (v2.19.0/2.19.1) */
    defaultLayout: {
      block: 'flex-screen-default-section-layout',
      fieldsGrid: 'flex-screen-default-layout-fields',
      /** Field inputs — suffix: widthKind|widthValue|maxWidth|minWidth|sectionHorizontalAlignment */
      inputPrefix: 'input-default-layout-',
      checkboxPrefix: 'checkbox-default-layout-',
      /** Per-profile override badge (v2.19.1) — same field suffixes */
      overrideBadgePrefix: 'default-layout-override-badge-',
      padding: 'flex-screen-default-section-padding',
      margin: 'flex-screen-default-section-margin',
    },
  },

  // ── Countdown Dialog ──
  countdownNameInput: 'countdown-name-input',
  countdownDurationInput: 'countdown-duration-input',
  countdownSubmitBtn: 'countdown-submit-btn',

  // ── Link Dialog ──
  linkNameInput: 'link-name-input',
  linkUrlInput: 'link-url-input',
  linkSubmitBtn: 'link-submit-btn',

  // ── Label Dialog ──
  labelNameInput: 'label-name-input',
  labelSubmitBtn: 'label-submit-btn',
  labelTextColor: 'input-style-label-block-text-color',
  labelBgColor: 'input-style-label-block-background-color',

  // ── Embed (WebEmbed / CustomEmbed) ──
  embed: {
    iframeToggle: 'embed-iframe-toggle',
    // The full FormControlLabel row (switch + "Render in iframe…" caption) — the wider
    // clip target so the per-setting image keeps the label, not just the bare switch.
    iframeRow: 'embed-iframe-row',
    scanImagesBtn: 'embed-scan-images-btn',
    // Wrapper testids added for the help-article block recipe (etalon-v2): the Monaco
    // HTML editor box and the Data Sources (child sections) region carry no native
    // testid, so the section component wraps them for a stable Level-C clip target.
    htmlEditor: 'embed-html-editor',
    dataSources: 'embed-data-sources',
  },

  // ── Paywall ──
  paywallMediaSettingsToggle: 'paywall-media-settings-toggle',
  paywallMediaGradient: 'paywall-media-gradient',
  paywallMediaGradientEnable: 'paywall-media-gradient-enable',
  paywallMediaGradientHeight: 'paywall-media-gradient-height-slider',
  paywallMediaGradientColor: 'input-style-paywall-gradient-color',
  bulletStyleSettingsBtn: 'bullet-style-settings-button',
  bulletStyleSections: 'bullet-style-sections',
  addBulletItem: 'add-bullet-item',
  /** Bullet item editor field: append `{index}-title` / `{index}-subtitle` */
  bulletItemPrefix: 'bullet-item-',
  addSubscriptionItem: 'add-subscription-item',
  subscriptionStyleSettingsBtn: 'subscription-style-settings-button',
  subscriptionStyleSections: 'subscription-style-sections',
  subscriptionColumnLayoutSection: 'subscription-style-section-columnLayout',
  subscriptionLeftColumnWidthInput: 'input-style-column-layout-left-column-width-',
  // Selected item state + Checkbox styles (SubscriptionStyleSections, ids from
  // createV2SubscriptionStyleSections; Checkbox styles shows only for CheckboxLabels / LabelsCheckbox).
  subscriptionSelectedStateSection: 'subscription-style-section-selectedState',
  subscriptionCheckboxStylesSection: 'subscription-style-section-checkboxStyles',
  // Color field wrappers (hex <input type="text"> nested): content.paywall.products.selectedAppearance.borders.color
  // and content.paywall.products.items.0.checkbox.selectedAppearance.color.
  paywallSelectedBorderColorInput: 'input-style-selected-state-border-color',
  paywallCheckedColorInput: 'input-style-checkbox-styles-checked-color',
  /** Subscription item: append `{index}-productId` */
  subscriptionItemPrefix: 'subscription-item-',
  /** Paywall variable menu item: append variable name */
  paywallVarPrefix: 'paywall-var-',

  /**
   * Inline paywall product picker (replaces the old MUI Select on each
   * subscription item). The `trigger` keeps the legacy testid
   * `subscription-item-{i}-productId` so existing helpers (e.g.
   * `configurePaywallSubscriptions`) still find the entry point. All other
   * testids are row-suffixed so multiple plan rows can have their pickers
   * expanded simultaneously without duplicate testids.
   */
  paywallProductPicker: {
    trigger: (i: number) => `subscription-item-${i}-productId`,
    panel: (i: number) => `paywall-product-picker-panel-${i}`,
    tabSandbox: (i: number) => `paywall-product-picker-tab-sandbox-${i}`,
    tabLive: (i: number) => `paywall-product-picker-tab-live-${i}`,
    search: (i: number) => `paywall-product-picker-search-${i}`,
    option: (i: number, productId: string) =>
      `paywall-product-picker-option-${i}-${productId}`,
    // Deterministic CSS locator for an option by its product NAME (the value the
    // test knows), via the `data-product-name` hook on the option row. Returns a
    // full CSS selector (not a testid) for use with page.locator(...). Avoids
    // resolving productId or fragile free-text matching. Quotes in the name are
    // escaped for the attribute selector.
    optionByName: (name: string) =>
      `[data-testid^="paywall-product-picker-option-"][data-product-name="${name.replace(/(["\\])/g, '\\$1')}"]`,
    typeChip: (i: number, productId: string) =>
      `paywall-product-picker-type-chip-${i}-${productId}`,
    trialChip: (i: number, productId: string) =>
      `paywall-product-picker-trial-chip-${i}-${productId}`,
    providerChip: (i: number, productId: string) => `paywall-product-picker-provider-${i}-${productId}`,
    providerFilter: (i: number, provider: 'all' | 'stripe' | 'paddle') => `paywall-product-picker-filter-${i}-${provider}`,
    providerWarning: (i: number) => `paywall-product-picker-provider-warning-${i}`,
    paddleStatus: (i: number) => `paywall-product-picker-paddle-status-${i}`,
    connectPaddle: (i: number) => `paywall-product-picker-connect-paddle-${i}`,
    createNewMenu: (i: number) => `paywall-product-picker-create-new-menu-${i}`,
    createNewMenuStripe: (i: number) => `paywall-product-picker-create-new-stripe-${i}`,
    createNewMenuPaddle: (i: number) => `paywall-product-picker-create-new-paddle-${i}`,
    createNewBtn: (i: number) => `paywall-product-picker-create-new-${i}`,
    mixedModeWarning: (i: number) => `paywall-product-picker-mixed-mode-warning-${i}`,
    loadingSpinner: (i: number) => `paywall-product-picker-loading-${i}`,
    emptyState: (i: number) => `paywall-product-picker-empty-${i}`,
    errorState: (i: number) => `paywall-product-picker-error-${i}`,
    retryBtn: (i: number) => `paywall-product-picker-retry-${i}`,
    // M7: inline Stripe Connect affordance — replaces disabled-tab tooltip
    // with a clickable Connect CTA banner in the panel content area.
    connectBanner: (i: number) => `paywall-product-picker-connect-banner-${i}`,
    connectBtn: (i: number, mode: 'test' | 'live') =>
      `paywall-product-picker-connect-btn-${i}-${mode}`,
    // Save-guard modal mounted from the picker before redirect to Stripe.
    // Modal is rendered via MUI Dialog portal; no row suffix needed (only
    // one modal at a time across the entire editor).
    connectConfirmDialog: 'paywall-product-picker-connect-confirm-dialog',
    connectConfirmSave: 'paywall-product-picker-connect-confirm-save',
    connectConfirmDiscard: 'paywall-product-picker-connect-confirm-discard',
    connectConfirmCancel: 'paywall-product-picker-connect-confirm-cancel',
  },
} as const;
